import { Component, Input, Output, EventEmitter, ViewChild, OnInit, OnDestroy, OnChanges, Inject, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { SaveService } from '../../../services/save.service';
import { DefaultValuesService } from '../../../services/defaultvalues.service';
import { CustomDialogService } from '../../../services/customdialog.service';
import { DialogService } from "../../../services/dialog.service";
import { Utils } from '../../../utils/utils';
import { MatDialog } from '@angular/material/dialog';
import { AutoRemoteResetCommandAddDialogComponent } from './add-commandkeypopup/autoremoteresetcommand-add-dialog.component';
import { AutoRemoteResetCommandConfig } from '../../../interfaces/autoremoteresetcommandconfig';
import { AllowedTimeRange } from '../../../interfaces/allowedtimerange';
import { LogsHistoryPopupComponent } from '../logshistorypopup/logshistorypopup.component';

@Component({
    selector: 'autoremoteresetcommand-list',
    templateUrl: './autoremoteresetcommand-list.component.html',
    styleUrls: ['./autoremoteresetcommand-list.component.less'],
    standalone: false
})

export class AutoRemoteResetCommandListComponent implements OnInit, OnDestroy, OnChanges {

    autoRemoteReset: AutoRemoteResetCommandConfig & { ScheduleConfigurations?: AllowedTimeRange[] } = {
        Stage: 1,
        IsAutoRemoteResetEnabled: false,
        ScheduleConfigurations: [
            {
                StartDayOfWeek: null,
                EndDayOfWeek: null,
            }
        ]
    } as any;

    selectedStage: number = 1;
    stages: { label: string, value: number, icon: string, cssClass: string }[] = [
        { label: 'STABLE', value: 0, icon: 'verified', cssClass: 'stable' },
        { label: 'PROD', value: 1, icon: 'rocket_launch', cssClass: 'prod' }
    ];



    @Input() list: any[] = [];
    @Output() onReload = new EventEmitter<any>();
    @Output() onGlobalReload = new EventEmitter<any>();
    columnsToDisplay = ['Sequence', 'Command', 'Action'];

    @ViewChild(MatTable, { static: true }) table!: MatTable<any>;

    public dataSource = new MatTableDataSource<any>();

    executionLimits: number[] = Array.from({ length: 21 }, (_, i) => i);

    subscriptionTenant: any;
    subscriptionControllerType: any;
    originalConfig: any[] = [];
    originalRowData: any;
    originalAutoRemoteReset: any = null;

    maxResetPerWeek: number = 0;
    maxResetPerDay: number = 0;
    maxResetPerHour: number = 0;

    alwaysAllowAutoReset: boolean = false;

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router,
        private readonly http: HttpClient,
        private readonly saveService: SaveService,
        public readonly defaults: DefaultValuesService,
        private readonly customDialog: CustomDialogService,
        private readonly dialogService: DialogService,
        @Inject('BASE_URL') private readonly baseUrl: string,
        private readonly changeDetectorRefs: ChangeDetectorRef,
        private readonly matDialog: MatDialog,
        private readonly dialog: MatDialog,
    ) {
        this.dialogService.tthis = this;
    }

    daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    minutes = Array.from({ length: 60 }, (_, i) => i);
    hours = Array.from({ length: 24 }, (_, i) => i);

    openLogsHistory() {
        this.dialog.open(LogsHistoryPopupComponent, {
            width: '80vw',
            maxHeight: '90vh',
            data: { 
                section: 'autoremoteresetcommand',
                tenantId: this.defaults.tenant,
                controllerTypeId: this.defaults.controllertype.ControllerTypeID
            }
        });
    }

    ngOnInit() {
        window.addEventListener('beforeunload', this.beforeUnloadHandler.bind(this));
        // Subscribe to tenant and controller type changes
        this.subscriptionTenant = this.defaults.tenantMessageChanges$.subscribe(() => {
            this.loadAutoRemoteResetConfig();
        });
        this.subscriptionControllerType = this.defaults.controllerTypeMessageChanges$.subscribe(() => {
            this.loadAutoRemoteResetConfig();
        });
        this.loadAutoRemoteResetConfig();
    }

    ngOnChanges(changes: SimpleChanges) {
        // Only update if the parent actually provides a new, non-empty list
        if (changes['list'] && changes['list'].currentValue && changes['list'].currentValue.length > 0) {
            this.dataSource.data = this.list;
        }
    }

    loadAutoRemoteResetConfig() {
        Utils.httpGetAutoRemoteResetCommandConfig(this.http, this.baseUrl, this, (tthis, result) => {
            if (result) {
                tthis.autoRemoteReset = result;
                tthis.autoRemoteReset.Stage = tthis.selectedStage;

                if (!tthis.autoRemoteReset.ScheduleConfigurations) {
                    tthis.autoRemoteReset.ScheduleConfigurations = [{
                        StartDayOfWeek: null,
                        EndDayOfWeek: null,
                        StartTime: { hours: 0, minutes: 0 },
                        EndTime: { hours: 0, minutes: 0 }
                    }];
                } else {
                    tthis.autoRemoteReset.ScheduleConfigurations = tthis.autoRemoteReset.ScheduleConfigurations.map(cfg => ({
                        ...cfg,
                        StartTime: tthis.parseTime(cfg.StartTimeOfDay),
                        EndTime: tthis.parseTime(cfg.EndTimeOfDay)
                    }));
                }
                // Ensure AutoRemotResetControllerCommandConfigurationId is preserved in dataSource
                tthis.dataSource.data = (result.RemoteResetSequences || []).map((row: any) => ({
                    ...row,
                    AutoRemotResetControllerCommandConfigurationId: row.AutoRemotResetControllerCommandConfigurationId ?? 0
                }));
                tthis.originalAutoRemoteReset = JSON.parse(JSON.stringify(tthis.autoRemoteReset)); // Deep copy

                console.log('Auto Remote Reset configuration loaded:', tthis.autoRemoteReset);
            } else {
                tthis.customDialog.openDisplayInfoDialog({
                    title: 'Error',
                    message: 'Failed to load Auto Remote Reset configuration'
                });
            }
        }, this.selectedStage);
    }

    ngOnDestroy() {
        window.removeEventListener('beforeunload', this.beforeUnloadHandler.bind(this));
        if (this.subscriptionTenant) {
            this.subscriptionTenant.unsubscribe();
        }
        if (this.subscriptionControllerType) {
            this.subscriptionControllerType.unsubscribe();
        }
    }

    moveUp(row: any) {
        const arr = this.dataSource.data;
        const index = arr.indexOf(row);
        if (index > 0) {
            // Swap the entire row (including CommandKey) with the previous row
            [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
            // Reassign Sequence and SequenceId to be 1-based and contiguous
            arr.forEach((item, idx) => {
                item.SequenceId = idx + 1;
            });
            this.dataSource.data = [...arr]; // Refresh table
            this.syncRemoteResetSequences();
        }
    }

    moveDown(row: any) {
        const arr = this.dataSource.data;
        const index = arr.indexOf(row);
        if (index < arr.length - 1) {
            // Swap the entire row (including CommandKey) with the next row
            [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
            // Reassign Sequence and SequenceId to be 1-based and contiguous
            arr.forEach((item, idx) => {
                item.SequenceId = idx + 1;
            });
            this.dataSource.data = [...arr]; // Refresh table
            this.syncRemoteResetSequences();
        }
    }
    deleteRow(row: any) {
        const arr = this.dataSource.data;
        const index = arr.indexOf(row);
        if (index > -1) {
            arr.splice(index, 1);
            // Reassign SequenceId to be 1-based and contiguous after deletion
            arr.forEach((item, idx) => item.SequenceId = idx + 1);
            this.dataSource.data = [...arr];
            this.syncRemoteResetSequences();
        }
    }

    editRow(row: any) {
        // this.dataSource.data.forEach(r => r.isEditing = false);
        this.originalRowData = { ...row };
        row.isEditing = true;
        this.dataSource.data = [...this.dataSource.data];
    }

    saveRow(row: any) {
        row.isEditing = false;
        this.dataSource.data = [...this.dataSource.data];
        this.syncRemoteResetSequences();
        this.onReload.emit(row);
    }

    cancelEdit(row: any) {
        if (this.originalRowData && row) {
            Object.keys(this.originalRowData).forEach(key => {
                row[key] = this.originalRowData[key];
            });
        }
        row.isEditing = false;
        this.dataSource.data = [...this.dataSource.data];
        this.syncRemoteResetSequences();
    }

    openAddCommandDialog() {
        const dialog = this.matDialog.open(AutoRemoteResetCommandAddDialogComponent, {
            panelClass: 'custom-mat-dialog',
            disableClose: true,
            data: { commandKeys: this.autoRemoteReset.RemoteResetCommandList }
        });
        dialog.afterClosed().subscribe(result => {
            if (result) {
                // Add the new command to the RemoteResetSequences in the model
                const newSequence = {
                    SequenceId: (this.autoRemoteReset.RemoteResetSequences?.length || 0) + 1,
                    CommandKey: result.Key,
                    AutoRemotResetControllerCommandConfigurationId: result.AutoRemotResetControllerCommandConfigurationId ?? 0
                };
                if (!this.autoRemoteReset.RemoteResetSequences) {
                    this.autoRemoteReset.RemoteResetSequences = [];
                }
                this.autoRemoteReset.RemoteResetSequences.push(newSequence);
                // Optionally update the table view if it is based on RemoteResetSequences
                this.dataSource.data = [...this.autoRemoteReset.RemoteResetSequences];
                this.syncRemoteResetSequences();
            }
        });
    }

    // Sync dataSource.data to autoRemoteReset.RemoteResetSequences for change tracking
    private syncRemoteResetSequences() {
        this.autoRemoteReset.RemoteResetSequences = [...this.dataSource.data];
    }

    getAutoRemoteResetConfiguration() {
        this.http
            .get<any>(
                this.baseUrl + Utils.getRemoteActionAPI() + Utils.getAutoRemoteResetCommandAPI
            ).subscribe((res) => {
                this.onReload.emit(res);
            }, (error: {
                error: { message: any }
            }) => {
                this.customDialog.openDisplayInfoDialog({ title: 'Error', message: error.error.message });
            });
    }
    addAllowedTimeRange() {
        this.autoRemoteReset.ScheduleConfigurations!.push({
            StartDayOfWeek: null,
            EndDayOfWeek: null,
            StartTime: { hours: 0, minutes: 0 },
            EndTime: { hours: 0, minutes: 0 },
            EndTimeOfDay: '00:00:00',
            StartTimeOfDay: '00:00:00',
        });
    }

    removeAllowedTimeRange(index: number) {
        this.autoRemoteReset.ScheduleConfigurations!.splice(index, 1);
    }

    onAlwaysAllowAutoResetChange() {
        if (this.alwaysAllowAutoReset) {
            this.autoRemoteReset.ScheduleConfigurations = [];
            this.autoRemoteReset.ScheduleConfigurations = [{
                StartDayOfWeek: 'Sunday',
                EndDayOfWeek: 'Saturday',
                StartTime: { hours: 0, minutes: 0 },
                EndTime: { hours: 23, minutes: 59 },
                EndTimeOfDay: '23:59:00',
                StartTimeOfDay: '00:00:00',
            }];
        }
    }

    onEnvironmentChange() {
        this.loadAutoRemoteResetConfig();
    }

    // Validation for start/end day, time, and holiday dates
    validateDayTimeHoliday(): boolean {
        if (this.autoRemoteReset.ScheduleConfigurations) {
            for (const range of this.autoRemoteReset.ScheduleConfigurations) {
                if (range.StartDayOfWeek && range.EndDayOfWeek) {
                    const startIdx = this.daysOfWeek.indexOf(range.StartDayOfWeek);
                    const endIdx = this.daysOfWeek.indexOf(range.EndDayOfWeek);
                    if (startIdx > endIdx) {
                        this.customDialog.openDisplayInfoDialog({
                            title: 'Validation Error',
                            message: 'Start day cannot be after End day in one of the allowed time ranges.'
                        });
                        return false;
                    }
                }
            }
        }
        return true;
    }

    // Ask for confirmation before saving
    saveAutoRemoteResetConfiguration() {
        if (!this.validateDayTimeHoliday()) return;
        if (this.hasUnsavedChanges()) {
            this.autoRemoteReset.ScheduleConfigurations = this.autoRemoteReset.ScheduleConfigurations?.filter(r =>
                r.StartDayOfWeek && r.EndDayOfWeek
            );

            this.autoRemoteReset.ScheduleConfigurations = this.autoRemoteReset.ScheduleConfigurations?.map(cfg => ({
                ...cfg,
                StartTimeOfDay: this.formatTime(cfg.StartTime),
                EndTimeOfDay: this.formatTime(cfg.EndTime)
            }));

            this.customDialog.openConfirm({
                title: "Save Auto Remote Reset changes",
                message: `Are you sure you want to save these changes?`,
                caller: this
            });

            this.customDialog.confirmed().subscribe(res => {
                if (res.confirmed) {
                    this.autoRemoteReset.Stage = this.selectedStage;
                    this.autoRemoteReset.RemoteResetSequences = this.dataSource.data.map((row: any, idx: number) => ({
                        SequenceId: row.Sequence ?? idx + 1,
                        CommandKey: row.CommandKey,
                        AutoRemotResetControllerCommandConfigurationId: row.AutoRemotResetControllerCommandConfigurationId ?? null
                    }));
                    Utils.httpAutoRemoteResetCommandConfig(
                        this.http,
                        this.baseUrl,
                        this.autoRemoteReset,
                        this,
                        (tthis, result) => {
                            tthis.customDialog.openDisplayInfoDialog({
                                title: 'Success',
                                message: 'Auto Remote Reset configuration saved successfully'
                            });
                            this.loadAutoRemoteResetConfig();
                        }
                    );
                }
            });
        }
        else {
            this.customDialog.openDisplayInfoDialog({
                title: 'No Changes',
                message: 'There are no unsaved changes to save.'
            });
        }
    }

    toggleAutoRemoteReset() {
        if (this.autoRemoteReset) {
            this.autoRemoteReset.IsAutoRemoteResetEnabled = !this.autoRemoteReset.IsAutoRemoteResetEnabled;
        }
    }

    parseTime(time: string): { hours: number, minutes: number } {
        const [hours, minutes] = time.split(':').map(Number);
        return { hours, minutes };
    }

    formatTime(time: { hours: number, minutes: number }): string {
        return `${time.hours.toString().padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}:00`;
    }

    getCommandDescription(commandKey: number): string {
        if (!this.autoRemoteReset || !this.autoRemoteReset.RemoteResetCommandList) return commandKey.toString();
        let keyStr = commandKey.toString();
        if (keyStr.length === 1) {
            keyStr = '0' + keyStr;
        }
        const cmd = this.autoRemoteReset.RemoteResetCommandList.find(c => c.Key === keyStr);
        return cmd ? cmd.Description : commandKey.toString();
    }

    hasUnsavedChanges(): boolean {
        return JSON.stringify(this.autoRemoteReset) !== JSON.stringify(this.originalAutoRemoteReset);
    }

    beforeUnloadHandler(event: any) {
        if (this.hasUnsavedChanges()) {
            event.preventDefault();
            event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
            return event.returnValue;
        }
    }

}